import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import i18n from '../../../i18n';
import { episodeScriptApi } from '../../../services/user';
import { IProjectAsset, AssetType, ScriptCharacter, ScriptProp, ScriptScene, AspectRatio, Resolution } from '@packages/global';

interface SelectedModels {
  llmModelId: string;
  llmVlModelId: string;
  text2imageModelId: string;
  imageEditModelId: string;
}

export function useVideoGeneration(projectId?: string) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  // 文件附件
  const [scriptFileUrl, setScriptFileUrl] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 主体选择
  const [selectedSubjects, setSelectedSubjects] = useState<IProjectAsset[]>([]);

  // 画面比例 & 分辨率
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState<Resolution>(Resolution.HD_720P);

  // 风格
  const [selectedStyle, setSelectedStyle] = useState('');

  // 目标视频时长（秒），null 表示 AI 自动决定
  const [targetDuration, setTargetDuration] = useState<number | null>(null);

  // 单个分镜最大时长（秒），null 表示不限制
  const [maxShotDuration, setMaxShotDuration] = useState<number | null>(null);

  // 模型
  const [selectedModels, setSelectedModels] = useState<SelectedModels>({
    llmModelId: '',
    llmVlModelId: '',
    text2imageModelId: '',
    imageEditModelId: ''
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExtensions = ['.txt', '.md', '.docx'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      alert(i18n.t('script:videoGeneration.unsupportedFormat'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert(i18n.t('script:videoGeneration.fileTooLarge'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('script', file);
      const response = await fetch('/api/user/upload/script', {
        method: 'POST',
        body: formData,
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const result = await response.json();
      if (result.success && result.data?.url) {
        setScriptFileUrl(result.data.url);
        setUploadedFileName(file.name);
      } else {
        alert(result.message || i18n.t('script:videoGeneration.uploadFailed'));
      }
    } catch {
      alert(i18n.t('script:videoGeneration.uploadFailedRetry'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClearFile = () => {
    setScriptFileUrl('');
    setUploadedFileName('');
  };

  const removeSubject = (id: string) => {
    setSelectedSubjects(prev => prev.filter(a => a.id !== id));
  };

  const addSubjects = (assets: IProjectAsset[]) => {
    setSelectedSubjects(prev => {
      const newAssets = assets.filter(a => !prev.some(p => p.id === a.id));
      return [...prev, ...newAssets];
    });
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      alert(i18n.t('script:videoGeneration.enterPrompt'));
      return;
    }
    setLoading(true);
    try {
      const characters: ScriptCharacter[] = [];
      const props: ScriptProp[] = [];
      const scenes: ScriptScene[] = [];

      selectedSubjects.forEach(asset => {
        // 保留 assetId（系统库主体标识）和 sourceAssetId（个人库继承来源）
        // 以便后端 AssetSyncService 写入 ProjectAsset 时维持血缘
        const baseData = {
          name: asset.name,
          prompt: asset.prompt || asset.name,
          imageUrl: asset.conceptImageUrl,
          assetId: asset.assetId,
          sourceAssetId: asset.id,  // 当前 asset.id 即为源主体 ID
        };
        switch (asset.assetType) {
          case AssetType.CHARACTER:
            characters.push({ ...baseData, audioUrl: asset.voiceReference, voiceId: asset.voiceModelId });
            break;
          case AssetType.PROP:
            props.push(baseData);
            break;
          case AssetType.SCENE:
            scenes.push(baseData);
            break;
        }
      });

      const result = await episodeScriptApi.createEpisode({
        prompt: prompt.trim(),
        scriptFileUrl: scriptFileUrl.trim() || undefined,
        imageModelId: selectedModels.text2imageModelId,
        llmModelId: selectedModels.llmModelId,
        llmVlModelId: selectedModels.llmVlModelId,
        aspectRatio: aspectRatio as AspectRatio,
        resolution,
        projectId,
        characters: characters.length > 0 ? characters : undefined,
        props: props.length > 0 ? props : undefined,
        scenes: scenes.length > 0 ? scenes : undefined,
        artStyle: selectedStyle || undefined,
        targetDuration: targetDuration ?? undefined,
        maxShotDuration: maxShotDuration ?? undefined
      });

      navigate(`/episode/${result.episodeId}/script-generation`);
    } catch (error: any) {
      alert(error.message || i18n.t('script:videoGeneration.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return {
    prompt, setPrompt,
    loading,
    scriptFileUrl, uploadedFileName, uploading, fileInputRef,
    handleFileUpload, handleClearFile,
    selectedSubjects, addSubjects, removeSubject,
    aspectRatio, setAspectRatio,
    resolution, setResolution,
    selectedStyle, setSelectedStyle,
    targetDuration, setTargetDuration,
    maxShotDuration, setMaxShotDuration,
    selectedModels, setSelectedModels,
    handleSubmit
  };
}
